var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from 'reselect';
import { buildSearchCasesAndColumns, enhanceDocumentWithFiles, getDocumentsByApplicant, getNumberedApplicantTooltip, parseInformationData, parseInformationTab, prepareDocumentFilesForView, } from './helpers/caseHelpers';
import { getKYCSessionByApplicant } from './helpers/qpcConverter';
import { colorOfHistoryEvent, labelOfHistoryEvent, shouldDisplayEvent } from 'src/redux/cases/helpers/historyHelpers';
import { getFileUrls, getI18n, getIdentityConfiguration, getSOA3Url } from '../core/coreSelectors';
import { flatArrays, getPropsByKeyPath, valueExist } from 'src/shared/helpers/objHelpers';
import { getPermissions, getUserInfo } from 'src/redux/auth/authSelectors';
import { E_ICONS } from 'src/ui/components/Icons/constants';
import { E_STATUS } from 'src/shared/types';
import { E_PERMISSION_KEY } from 'src/services/auth/types/permissionsTypes';
import { INFORMATION_IDS_IDENTITIES } from 'src/redux/cases/constants';
import { MAX_APPLICANTS_TO_DISPLAY } from 'src/shared/constants';
var casesState = function (state) { return state.cases; };
export var getFilters = createSelector(casesState, function (state) { return state.collection.filters; });
export var getAllColumns = createSelector(casesState, function (state) { return state.allColumns; });
export var getDisplayedColumns = createSelector(casesState, function (state) { return state.collection.columns; });
export var getDefaultColumns = createSelector(casesState, function (state) { return state.collection.defaultColumns; });
export var getAllQueries = createSelector(casesState, function (state) {
    return state.allQueries.sort(function (a, b) { var _a; return (_a = a.label) === null || _a === void 0 ? void 0 : _a.localeCompare(b.label); });
});
export var getQueriesSelected = createSelector(casesState, function (state) { var _a, _b; return ((_b = (_a = state.collection) === null || _a === void 0 ? void 0 : _a.filters) === null || _b === void 0 ? void 0 : _b.query) || []; });
export var getQueryListForFilterEdit = createSelector(casesState, function (state) {
    return state.allQueries.filter(function (query) { return !query.sticky; });
});
export var getQuerySelectedLabel = createSelector(casesState, getAllQueries, getQueriesSelected, function (state, allQueries, queriesSelected) {
    return queriesSelected.map(function (item) { var _a; return (_a = allQueries.find(function (allQueryItem) { return allQueryItem.id === item.id; })) === null || _a === void 0 ? void 0 : _a.label; });
});
export var getQueriesToShow = createSelector(casesState, getQueriesSelected, function (state, querySelected) {
    var queriesSticky = state.allQueries.filter(function (query) { return query.sticky; });
    // should reduce to filter out queries whose id is in queriesSticky
    var querySelectedEnhanced = querySelected.reduce(function (acc, item) {
        if (queriesSticky.some(function (qSticky) { return qSticky.id === item.id; })) {
            return acc;
        }
        return __spreadArray(__spreadArray([], acc, true), [state.allQueries.find(function (allQueryItem) { return allQueryItem.id === item.id; })], false);
    }, []);
    return __spreadArray(__spreadArray([], queriesSticky, true), querySelectedEnhanced, true);
});
export var getTotalCases = createSelector(casesState, function (casesState) { return casesState.collection.total; });
export var getCollectionResults = createSelector(casesState, function (casesState) { return casesState.collection.results; });
export var getCollectionStatus = createSelector(casesState, function (casesState) { return casesState.collection.status; });
export var getCasesCollection = createSelector(casesState, getFilters, getDisplayedColumns, getI18n, getUserInfo, function (state, filters, columns, i18n, userInfo) {
    if (filters) {
        return {
            collection: buildSearchCasesAndColumns(userInfo, state.collection.results, columns, i18n),
            nbOfPages: Math.ceil(state.collection.total / filters.count),
            currentPages: filters.offset ? filters.offset / filters.count + 1 : 1,
            nbByPage: filters.count,
            total: state.collection.total,
        };
    }
    return {
        collection: [],
        nbOfPages: 0,
        nbByPage: 0,
        currentPages: 1,
        total: 0,
    };
});
export var getCasesCollectionLoading = createSelector(casesState, function (state) { return state.collection.isLoading; });
export var getExportCasesLoading = createSelector(casesState, function (state) { return state.exportIsLoading; });
export var getCurrentTemplate = createSelector(casesState, function (state) { return state.currentTemplate; });
export var getCurrentApplicantId = createSelector(casesState, function (state) { return state.currentApplicantId; });
export var getCurrentCaseApplicants = createSelector(casesState, function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.current) === null || _a === void 0 ? void 0 : _a.applicants; });
export var getCurrentCaseHardData = createSelector(casesState, function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.current) === null || _a === void 0 ? void 0 : _a.hardData; });
export var getCurrentCase = createSelector(casesState, function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.current) === null || _a === void 0 ? void 0 : _a.data; });
export var getCurrentCaseLoading = createSelector(casesState, function (state) { var _a; return (_a = state === null || state === void 0 ? void 0 : state.current) === null || _a === void 0 ? void 0 : _a.isLoading; });
export var getCurrentCaseDocuments = createSelector(casesState, function (state) { return state.current.documents; });
export var getCurrentApplicant = createSelector([getCurrentCaseApplicants, getCurrentApplicantId], function (applicants, currentApplicantId) {
    if (!currentApplicantId) {
        return applicants[0];
    }
    var applicant = applicants.find(function (applicant) { return applicant.id === currentApplicantId; });
    return applicant || applicants[0];
});
export var getAllCurrentCaseHistoryFilters = createSelector(casesState, function (state) { var _a; return ((_a = state.current.history) === null || _a === void 0 ? void 0 : _a.filters.all) || []; });
export var getCurrentCaseHistoryFilters = createSelector(casesState, function (state) { var _a; return ((_a = state.current.history) === null || _a === void 0 ? void 0 : _a.filters.current) || []; });
export var getCurrentCaseLock = createSelector(casesState, function (state) { return state.current.lock; });
export var getCurrentCaseHistory = createSelector(casesState, getI18n, getCurrentCaseHistoryFilters, function (state, i18n, historyFilter) {
    var _a;
    var history = (_a = state === null || state === void 0 ? void 0 : state.current) === null || _a === void 0 ? void 0 : _a.history.events;
    if (history.length) {
        return history
            .filter(function (historyEvent) { return shouldDisplayEvent(historyEvent, historyFilter); })
            .map(function (historyEvent) { return (__assign(__assign({}, historyEvent), { color: colorOfHistoryEvent(historyEvent.eventType), label: labelOfHistoryEvent(i18n, historyEvent.eventType) })); })
            .sort(function (a, b) { return (b.dateOfEvent.ts - a.dateOfEvent.ts > 0 ? 1 : -1); });
    }
    else {
        return [];
    }
});
export var getCurrentCaseOverviewInfo = createSelector(getCurrentCase, getI18n, getCurrentCaseApplicants, function (currentCase, i18n, applicants) {
    var _a;
    var process = getPropsByKeyPath(i18n.keyValueMap, "caseGlobalView.moStatus.".concat(currentCase.moStatus, ".label"));
    var translatedAbortReason = getPropsByKeyPath(i18n.keyValueMap, "filter.moStatus.details.".concat(currentCase.moStatusDetails));
    var abortReason = translatedAbortReason ? translatedAbortReason : currentCase.moStatusDetails;
    var title = (_a = currentCase.pageTitle) === null || _a === void 0 ? void 0 : _a.replace('$caseReference', currentCase.caseReference);
    return {
        title: title,
        status: currentCase.pageSubTitle,
        process: "".concat(process ? "".concat(process) : '', " ").concat(abortReason ? "".concat(abortReason) : ''),
        nbOfStudy: currentCase.studyNumberV2 || currentCase.studyNumber,
        nbOfApplicants: (applicants === null || applicants === void 0 ? void 0 : applicants.length) || 0,
    };
});
export var getCurrentCaseInformation = createSelector([getCurrentCase, getCurrentCaseApplicants, getIdentityConfiguration, getI18n, getCurrentApplicant], function (currentCase, applicants, identityConfiguration, i18n, currentApplicant) {
    var compressedTooltipTemplate = getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.caseInformation.tabs.applicantNumbered');
    var tabs = parseInformationTab(currentCase === null || currentCase === void 0 ? void 0 : currentCase.caseInformationWidget, applicants, identityConfiguration === null || identityConfiguration === void 0 ? void 0 : identityConfiguration.display, currentApplicant, compressedTooltipTemplate);
    var infoTabs = parseInformationData(currentCase === null || currentCase === void 0 ? void 0 : currentCase.caseInformationWidget, applicants);
    var idsToRemove = __spreadArray([], INFORMATION_IDS_IDENTITIES, true);
    var infoTabsFilteredByPermission = infoTabs.map(function (infoItem) { return (__assign(__assign({}, infoItem), { data: infoItem.data
            .map(function (dataItem) {
            return dataItem
                .map(function (subDataItem) {
                return subDataItem
                    .filter(function (subItem) { return !idsToRemove.includes(subItem.key); })
                    .map(function (subItem) {
                    var isKeyGender = subItem.key.includes('gender');
                    if (isKeyGender) {
                        return __assign(__assign({}, subItem), { value: getPropsByKeyPath(i18n.keyValueMap, "common.gender.".concat(subItem.value)) || null });
                    }
                    else {
                        return subItem;
                    }
                });
            })
                .filter(function (subDataItem) { return subDataItem.length > 0; });
        })
            .filter(function (dataItem) { return dataItem.length > 0; }) })); });
    return { tabs: tabs, infoTabs: infoTabsFilteredByPermission };
});
export var getCurrentCaseDocumentsEnhanced = createSelector(casesState, getCurrentApplicant, getFileUrls, getCurrentCaseLock, function (state, currentApplicant, filesUrls, currentLock) {
    var _a = state.current, studyElements = _a.studyElements, data = _a.data;
    // convert studyElements to old format
    var documents = getDocumentsByApplicant(studyElements);
    var _b = data.documentsWidget, updateDocumentCategoryStatusLink = _b.updateDocumentCategoryStatusLink, updateProofElementStatusUrl = _b.updateProofElementStatusUrl;
    var shouldDisableDecision = (currentLock === null || currentLock === void 0 ? void 0 : currentLock.isLocked) || (!updateProofElementStatusUrl && !updateDocumentCategoryStatusLink);
    if (!currentApplicant) {
        return [];
    }
    var applicantDocuments = documents[currentApplicant.id];
    if (!applicantDocuments) {
        return [];
    }
    // @ts-ignore, Hide upload history, In the future we could display it
    var documentByApplicantWithFile = applicantDocuments.documents
        .filter(function (doc) { return !doc.isHistory; })
        .map(function (doc) { return (__assign(__assign({}, doc), { files: enhanceDocumentWithFiles(doc.files, filesUrls) })); });
    var mergeFileNeedToBeMerged = documentByApplicantWithFile.map(prepareDocumentFilesForView);
    var updatedDocumentWithDecision = mergeFileNeedToBeMerged.map(function (item) { return (__assign(__assign({}, item), { decisionDisabled: item.decisionDisabled || shouldDisableDecision })); });
    return updatedDocumentWithDecision;
});
export var getCurrentCaseKycSessions = createSelector(casesState, getCurrentCaseApplicants, function (state, applicants) {
    var kycSessions = state.current.data.kycSessions;
    return getKYCSessionByApplicant(applicants, kycSessions);
});
export var getCurrentCaseActions = createSelector(casesState, function (state) {
    var actions = state.current.actions;
    return actions;
});
export var getCurrentCaseActionModalContent = createSelector(casesState, function (state) {
    var actionModal = state.current.actionModal;
    return actionModal.content;
});
export var getCurrentCaseActionModalIsLoading = createSelector(casesState, function (state) {
    var actionModal = state.current.actionModal;
    return actionModal.isLoading;
});
export var getNBStatusRecapOfDocument = createSelector(casesState, function (state) {
    var documents = state.current.documents;
    var allStatusByApplicant = flatArrays(Object.values(documents).map(function (docByApplicant) { return docByApplicant.documents.map(function (doc) { return doc.status; }); }));
    var okStatus = allStatusByApplicant.filter(function (item) { return item.value === E_STATUS.OK; });
    var unknownStatus = allStatusByApplicant.filter(function (item) { return item.value === E_STATUS.UNKNOWN; });
    var koStatus = allStatusByApplicant.filter(function (item) { return item.value === E_STATUS.KO; });
    return {
        okStatus: okStatus.length,
        unknownStatus: unknownStatus.length,
        koStatus: koStatus.length,
    };
});
export var getCaseContracts = createSelector(casesState, getFileUrls, function (state, fileUrls) {
    var _a, _b;
    var contractsEnhancedWithFile = (_b = (_a = state === null || state === void 0 ? void 0 : state.current) === null || _a === void 0 ? void 0 : _a.contracts) === null || _b === void 0 ? void 0 : _b.map(function (contract) { return (__assign(__assign({}, contract), { contracts: contract.contracts.map(function (contractFile) {
            var contractWithFile = fileUrls[contractFile.fileUrl];
            // if fileUrl is not found in fileUrls state meaning fetch are not triggered yet
            if (!contractWithFile) {
                return __assign(__assign({}, contractFile), { src: null, isLoading: true, isError: false });
            }
            var _a = contractWithFile || {}, file = _a.file, isLoading = _a.isLoading, isError = _a.isError;
            return __assign(__assign({}, contractFile), { src: !file ? null : file, isLoading: isLoading, isError: isError || (valueExist(isLoading) && isLoading === false && !!!file) });
        }) })); });
    return contractsEnhancedWithFile || [];
});
export var getNotificationsActivated = createSelector(casesState, function (casesState) {
    return casesState.isNotificationsActivated;
});
export var getCurrentCaseComments = createSelector(casesState, function (casesState) {
    return casesState.current.comments;
});
export var getSOA3UploadUrl = createSelector(casesState, getSOA3Url, function (casesState, soa3Url) {
    return "".concat(soa3Url).concat(casesState.current.soa3UploadPath);
});
export var getCaseDocumentWidgetTab = createSelector(getCurrentCaseApplicants, getCurrentCaseHistory, getI18n, getPermissions, getSOA3UploadUrl, getCurrentApplicant, function (currentApplicants, currentHistory, i18n, permissions, soa3UploadUrl, currentApplicant) {
    var isLimitApplicantsReached = currentApplicants.length > MAX_APPLICANTS_TO_DISPLAY;
    var isMultiApplicants = currentApplicants.length > 1;
    var hasMoreThanTwoApplicants = currentApplicants.length > 2;
    var displayPermissionUploadModule = permissions
        ? permissions[E_PERMISSION_KEY.CASE_GLOBAL_VIEW_DISPLAY_UPLOAD_MODULE]
        : null;
    var isUploadModuleAllowed = displayPermissionUploadModule ? displayPermissionUploadModule.allowed : false;
    var applicantTabKey = "documents.tabs.".concat(isMultiApplicants ? 'applicant1' : 'applicant');
    var defaultApplicantTabLabel = getPropsByKeyPath(i18n, "keyValueMap.caseGlobalView.widgets.".concat(applicantTabKey));
    var numberedTooltipTemplate = getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.documents.tabs.applicantNumbered');
    var hasNumberedTooltip = isLimitApplicantsReached && !!currentApplicant && !!numberedTooltipTemplate;
    var applicantTooltip = hasNumberedTooltip
        ? getNumberedApplicantTooltip(numberedTooltipTemplate, currentApplicants, currentApplicant)
        : defaultApplicantTabLabel;
    var documentWidgetTabs = [
        {
            id: '1',
            icon: E_ICONS.USER,
            label: defaultApplicantTabLabel,
            tooltip: applicantTooltip,
            applicantId: currentApplicants[0].id,
        },
    ];
    if (isMultiApplicants && !isLimitApplicantsReached) {
        documentWidgetTabs.push({
            id: '2',
            icon: E_ICONS.GROUP,
            label: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.documents.tabs.applicant2'),
            tooltip: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.documents.tabs.applicant2'),
            applicantId: currentApplicants[1].id,
        });
    }
    if (hasMoreThanTwoApplicants && !isLimitApplicantsReached) {
        documentWidgetTabs.push({
            id: '3',
            icon: E_ICONS.GROUP,
            label: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.documents.tabs.applicant3'),
            tooltip: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.documents.tabs.applicant3'),
            applicantId: currentApplicants[2].id,
        });
    }
    documentWidgetTabs.push({
        id: '4',
        icon: E_ICONS.DOWNLOAD,
        label: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.documents.tabs.uploadState'),
        tooltip: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.documents.tabs.uploadState'),
    });
    if (isUploadModuleAllowed && !soa3UploadUrl.includes('null') && !soa3UploadUrl.includes('undefined')) {
        documentWidgetTabs.push({
            id: '5',
            icon: E_ICONS.EXPORT,
            label: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.upload.title'),
            tooltip: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.upload.title'),
        });
    }
    if (!!currentHistory) {
        documentWidgetTabs.push({
            id: '6',
            icon: E_ICONS.HISTORY,
            label: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.history.title'),
            tooltip: getPropsByKeyPath(i18n, 'keyValueMap.caseGlobalView.widgets.history.title'),
        });
    }
    return documentWidgetTabs;
});
export var getModalCache = createSelector(casesState, function (casesState) {
    return casesState.current.modal.cache;
});
export var getCurrentUpdatedDocumentData = createSelector(getCurrentApplicant, function (currentApplicant) {
    return currentApplicant === null || currentApplicant === void 0 ? void 0 : currentApplicant.updatedDocumentData;
});
export var getAutoPlay = createSelector(casesState, function (casesState) { return casesState.autoPlay; });
